/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useContext } from 'react';
import { graphql } from 'react-apollo';

import { compose } from 'recompose';

import { Grid, PageGridContainer, Typography, breakpoints } from '@coursera/cds-core';

import { DomainGetAllQuery } from 'bundles/browse/components/Queries';
import PageLoadEventContext from 'bundles/page/contexts/PageLoadEventContext';

import _t from 'i18n!nls/front-page';

import DomainPill from './DomainPill';

// https://tools.coursera.org/mega/query/_GjvkCHuEequ0VfWCrq7Lg@1#table gives this order for domain popularity
const domainImageMap = [
  {
    id: 'data-science',
    title: _t('Data Science'),
    url: 'https://s3.amazonaws.com/coursera_assets/browse/domains/data_science.png',
    bannerUrl: 'https://s3.amazonaws.com/coursera_assets/browse/domain-banner/data_science.png',
  },
  {
    id: 'business',
    title: _t('Business'),
    url: 'https://s3.amazonaws.com/coursera_assets/browse/domains/business.png',
    bannerUrl: 'https://s3.amazonaws.com/coursera_assets/browse/domain-banner/business.png',
  },
  {
    id: 'computer-science',
    title: _t('Computer Science'),
    url: 'https://s3.amazonaws.com/coursera_assets/browse/domains/computer_science.png',
    bannerUrl: 'https://s3.amazonaws.com/coursera_assets/browse/domain-banner/computer_science.png',
  },
  {
    id: 'life-sciences',
    title: _t('Health'),
    url: 'https://s3.amazonaws.com/coursera_assets/browse/domains/health.png',
    bannerUrl: 'https://s3.amazonaws.com/coursera_assets/browse/domain-banner/health.png',
  },
  {
    id: 'social-sciences',
    title: _t('Social Sciences'),
    url: 'https://s3.amazonaws.com/coursera_assets/browse/domains/social_sciences.png',
    bannerUrl: 'https://s3.amazonaws.com/coursera_assets/browse/domain-banner/social_sciences.png',
  },
  {
    id: 'personal-development',
    title: _t('Personal Development'),
    url: 'https://s3.amazonaws.com/coursera_assets/browse/domains/personal_development.png',
    bannerUrl: 'https://s3.amazonaws.com/coursera_assets/browse/domain-banner/personal_development.png',
  },
  {
    id: 'arts-and-humanities',
    title: _t('Arts and Humanities'),
    url: 'https://s3.amazonaws.com/coursera_assets/browse/domains/arts_and_humanities.png',
    bannerUrl: 'https://s3.amazonaws.com/coursera_assets/browse/domain-banner/arts_and_humanities.png',
  },
  {
    id: 'physical-science-and-engineering',
    title: _t('Phsyical Science and Engineering'),
    url: 'https://s3.amazonaws.com/coursera_assets/browse/domains/physical_science_and_engineering.png',
    bannerUrl: 'https://s3.amazonaws.com/coursera_assets/browse/domain-banner/physical_science_and_engineering.png',
  },
  {
    id: 'language-learning',
    title: _t('Language Learning'),
    url: 'https://s3.amazonaws.com/coursera_assets/browse/domains/language_learning.png',
    bannerUrl: 'https://s3.amazonaws.com/coursera_assets/browse/domain-banner/language_learning.png',
  },
  {
    id: 'information-technology',
    title: _t('Information Technology'),
    url: 'https://s3.amazonaws.com/coursera_assets/browse/domains/information_technology.png',
    bannerUrl: 'https://s3.amazonaws.com/coursera_assets/browse/domain-banner/information_technology.png',
  },
  {
    id: 'math-and-logic',
    title: _t('Math and Logic'),
    url: 'https://s3.amazonaws.com/coursera_assets/browse/domains/math_and_logic.png',
    bannerUrl: 'https://s3.amazonaws.com/coursera_assets/browse/domain-banner/math_and_logic.png',
  },
];

type Domain = {
  id: string;
  slug: string;
  name: string;
};

type GraphqlOptionsProps = {
  data?: { DomainsV1Resource?: { domains: { elements: Domain[] } }; loading: boolean };
};

type Props = {
  domainsList: Domain[];
};

const styles = {
  container: css`
    margin: 0 auto;
    margin-bottom: 9rem;

    ${breakpoints.down('xs')} {
      margin: 0 auto var(--cds-spacing-800);
    }
  `,
  title: css`
    margin-bottom: 16px;
  `,
};

const getDomainsToShow = (domainsList: Domain[]) => {
  return domainImageMap.map((domain) => {
    return {
      ...domain,
      ...(domainsList || []).filter((topic) => topic.id === domain.id)[0],
      imageUrl: domain.url,
      name: domain.title,
    };
  });
};

const renderPills = (domainsList: Domain[], hasPageLoaded: boolean) => {
  return getDomainsToShow(domainsList).map((domain) => (
    <DomainPill key={domain.id} useLazyImage={!hasPageLoaded} {...domain} />
  ));
};

export const DomainPillsContainer = ({ domainsList }: Props) => {
  const { hasPageLoaded } = useContext(PageLoadEventContext);

  return (
    <section css={styles.container}>
      <PageGridContainer>
        <Grid item xs={12}>
          <Typography component="h2" variant="d2" css={styles.title}>
            {_t('Explore Coursera')}
          </Typography>
        </Grid>
        <Grid container item xs={12} wrap="wrap" alignItems="stretch">
          {renderPills(domainsList, hasPageLoaded)}
        </Grid>
      </PageGridContainer>
    </section>
  );
};

const DomainGraphqlOptions = {
  options: {
    fetchPolicy: 'cache-first' as const,
    ssr: false,
  },
  props: ({ data }: GraphqlOptionsProps) => ({
    domainsList: data?.loading ? [] : data?.DomainsV1Resource?.domains?.elements,
  }),
};

export default compose<Props, {}>(graphql(DomainGetAllQuery, DomainGraphqlOptions))(DomainPillsContainer);
